console.log('product-collection-card.js loaded');

/**
 * Product Collection Card Enhancement
 * Handles: color swatches, sizing panel, image carousel (desktop arrows + mobile native scroll)
 */

export function initCollectionCard(cardElement) {
  const card = new CollectionCard(cardElement);
  card.init();
  return card;
}

class CollectionCard {
  constructor(element) {
    this.element = element;
    this.currentProductHandle = element.dataset.productHandle || '';
    this.currentImageIndex = 0;
    this.sizingPanelOpen = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isMobile = window.innerWidth < 1024;
    
    // DOM elements
    this.swatches = element.querySelectorAll('.collection-card-swatch');
    this.sizingPanel = element.querySelector('.collection-card-sizing-panel');
    this.sizingButtons = element.querySelectorAll('.collection-card-size-btn');
    this.sizingCloseBtn = element.querySelector('.collection-card-sizing-close');
    this.sizingTriggerBtn = element.querySelector('.collection-card-size-trigger');
    this.carouselPrevBtn = element.querySelector('.collection-card-carousel-prev');
    this.carouselNextBtn = element.querySelector('.collection-card-carousel-next');
    this.progressBar = element.querySelector('.collection-card-progress-bar');
    this.mediaWrapper = element.querySelector('.tile-media-wrapper');
    
    // Desktop: Use original selector pattern that finds all images in media wrapper
    this.mediaImages = element.querySelectorAll('.collection-card-desktop-media img');
    
    // Mobile carousel elements
    this.mobileCarousel = element.querySelector('.collection-card-mobile-carousel');
    this.carouselTrack = element.querySelector('.collection-card-carousel-track');
    
    // Data
    this.swatchData = new Map();
    this.currentVariantMapping = {};
    this.currentImages = [];
    this.totalImages = 0;
    this.carouselActive = false;
    
    // Preload tracking
    this.preloadedImages = new Set();
  }

  init() {
    this.buildSwatchData();
    this.initializeImages();
    this.bindEvents();
    this.updateProgressBar();

    // Grey out initial compare-at prices
    this.greyOutCompareAtPrice();
    
    // Preload first few images on mobile
    if (this.isMobile) {
      this.preloadAdjacentImages(0);
    }
  }

  /**
   * Initialize images from swatches or card data attribute
   */
  initializeImages() {
    if (this.currentImages.length > 0) return;

    if (this.element.dataset.productImages) {
      try {
        this.currentImages = JSON.parse(this.element.dataset.productImages);
        this.totalImages = this.currentImages.length;
      } catch (e) {
        // Could not parse images
      }
    }
  }

  /**
   * Preload adjacent images for smoother scrolling
   * @param {number} currentIndex - Current slide index
   */
  preloadAdjacentImages(currentIndex) {
    // Preload next 2 images
    const indicesToPreload = [
      currentIndex + 1,
      currentIndex + 2
    ];
    
    indicesToPreload.forEach(index => {
      if (index >= 0 && index < this.totalImages && !this.preloadedImages.has(index)) {
        const imageUrl = this.currentImages[index];
        if (imageUrl) {
          const img = new Image();
          img.src = imageUrl;
          this.preloadedImages.add(index);
        }
      }
    });
  }

  /**
   * Build map of all sibling data from swatch elements
   */
  buildSwatchData() {
    this.swatches.forEach(swatch => {
      const handle = swatch.dataset.productHandle;
      const variants = swatch.dataset.variants ? JSON.parse(swatch.dataset.variants) : {};
      const images = swatch.dataset.imagesJson ? JSON.parse(swatch.dataset.imagesJson) : [];
      const url = swatch.dataset.productUrl || '';
      const color = swatch.dataset.productColor || '';
      const tags = swatch.dataset.productTags ? JSON.parse(swatch.dataset.productTags) : [];
      const price = swatch.dataset.price || '';
      const compareAtPrice = swatch.dataset.compareAtPrice || '';
      const currencySymbol = swatch.dataset.currencySymbol || '$';
      const currencyCode = swatch.dataset.currencyCode || 'AUD';

      this.swatchData.set(handle, {
        variants,
        images,
        url,
        color,
        handle,
        tags,
        price,
        compareAtPrice,
        currencySymbol,
        currencyCode
      });

      if (swatch.dataset.active === 'true') {
        this.currentProductHandle = handle;
        this.currentVariantMapping = {};
        for (const [size, data] of Object.entries(variants)) {
          this.currentVariantMapping[size] = typeof data === 'object' ? data.id : data;
        }
        this.currentImages = images;
        this.totalImages = images.length;
        this.currentImageIndex = 0;
      }
    });
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Color swatches
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => this.handleSwatchClick(e));
    });

    // Sizing panel
    if (this.sizingCloseBtn) {
      this.sizingCloseBtn.addEventListener('click', (e) => this.closeSizingPanel(e));
    }

    if (this.sizingTriggerBtn) {
      this.sizingTriggerBtn.addEventListener('click', (e) => this.openSizingPanel(e));
    }

    // Size buttons
    this.sizingButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleSizeClick(e));
    });

    // Desktop: Carousel arrows
    if (this.carouselPrevBtn) {
      this.carouselPrevBtn.addEventListener('click', (e) => this.prevImage(e));
    }

    if (this.carouselNextBtn) {
      this.carouselNextBtn.addEventListener('click', (e) => this.nextImage(e));
    }

    // Desktop: hover for sizing panel
    if (!this.isMobile) {
      this.element.addEventListener('mouseenter', () => this.showSizingPanel());
      this.element.addEventListener('mouseleave', () => {
        this.hideSizingPanel();
        this.deactivateCarousel();
      });
    }

    // Mobile: Native scroll - listen for scroll to update progress bar
    if (this.carouselTrack) {
      this.carouselTrack.addEventListener('scroll', () => this.handleMobileScroll(), { passive: true });
      
      // Mobile: Tap detection for navigation (tap = navigate, swipe = scroll)
      this.setupMobileTapDetection();
    }

    // Mobile: close sizing panel when visible image area tapped
    if (this.isMobile && this.mediaWrapper) {
      this.mediaWrapper.addEventListener('click', (e) => {
        if (this.sizingPanelOpen && !e.target.closest('.collection-card-sizing-panel')) {
          this.closeSizingPanel(e);
        }
      });
    }

    // Prevent propagation to product link
    this.sizingPanel?.addEventListener('click', (e) => e.stopPropagation());
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => e.stopPropagation());
    });

    // Handle resize
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 1024;
      
      // If switching from desktop to mobile or vice versa, reset state
      if (wasMobile !== this.isMobile) {
        this.currentImageIndex = 0;
        this.carouselActive = false;
        this.element.classList.remove('carousel-active');
      }
    });
  }

  /**
   * Handle mobile carousel scroll - update progress bar and preload adjacent images
   */
  handleMobileScroll() {
    if (!this.carouselTrack || !this.progressBar || this.totalImages <= 1) return;
    
    const scrollLeft = this.carouselTrack.scrollLeft;
    const scrollWidth = this.carouselTrack.scrollWidth;
    const clientWidth = this.carouselTrack.clientWidth;
    const maxScroll = scrollWidth - clientWidth;
    
    if (maxScroll <= 0) return;
    
    // Calculate which slide we're closest to
    const slideWidth = clientWidth;
    const currentIndex = Math.round(scrollLeft / slideWidth);
    this.currentImageIndex = Math.min(currentIndex, this.totalImages - 1);
    
    // Update progress bar
    const progress = ((this.currentImageIndex + 1) / this.totalImages) * 100;
    this.progressBar.style.width = `${progress}%`;
    
    // Preload adjacent images for smoother scrolling
    this.preloadAdjacentImages(this.currentImageIndex);
  }

  /**
   * Setup tap detection on mobile carousel
   * Distinguishes between taps (navigate) and swipes (scroll)
   */
  setupMobileTapDetection() {
    if (!this.carouselTrack) return;
    
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    
    this.carouselTrack.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
    }, { passive: true });
    
    this.carouselTrack.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const touchEndTime = Date.now();
      
      const deltaX = Math.abs(touchEndX - touchStartX);
      const deltaY = Math.abs(touchEndY - touchStartY);
      const deltaTime = touchEndTime - touchStartTime;
      
      // If minimal movement and quick tap, navigate to product
      // Thresholds: less than 15px movement, less than 250ms duration
      if (deltaX < 15 && deltaY < 15 && deltaTime < 250) {
        const productUrl = this.element.dataset.productUrl;
        if (productUrl) {
          window.location.href = productUrl;
        }
      }
    }, { passive: true });
  }

  /**
   * Activate carousel mode (hides hover overlay)
   */
  activateCarousel() {
    if (this.carouselActive) return;
    
    this.carouselActive = true;
    this.element.classList.add('carousel-active');
  }

  /**
   * Deactivate carousel mode (restores hover overlay)
   */
  deactivateCarousel() {
    this.carouselActive = false;
    this.element.classList.remove('carousel-active');
    this.currentImageIndex = 0;
    this.updateImageDisplay();
  }

  /**
   * Show previous image (Desktop)
   */
  prevImage(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (this.totalImages <= 1) return;
    
    // If carousel not yet active, user is viewing image 2 via hover overlay
    // So "previous" should take them to image 1 (index 0)
    if (!this.carouselActive) {
      this.currentImageIndex = 0;
    } else {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      } else {
        this.currentImageIndex = this.totalImages - 1;
      }
    }
    
    this.updateImageAndActivate();
  }

  /**
   * Show next image (Desktop)
   */
  nextImage(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (this.totalImages <= 1) return;
    
    // If carousel not yet active, user is viewing image 2 via hover overlay
    // So "next" should take them to image 3 (index 2)
    if (!this.carouselActive) {
      this.currentImageIndex = 2 < this.totalImages ? 2 : 0;
    } else {
      if (this.currentImageIndex < this.totalImages - 1) {
        this.currentImageIndex++;
      } else {
        this.currentImageIndex = 0;
      }
    }
    
    this.updateImageAndActivate();
  }

  /**
   * Update image and activate carousel only after new image is ready (Desktop)
   */
  updateImageAndActivate() {
    if (this.totalImages === 0 || this.currentImages.length === 0) return;

    const image = this.currentImages[this.currentImageIndex];
    if (!image) return;

    if (this.mediaImages.length > 0) {
      const mainImage = this.mediaImages[0];
      if (mainImage && mainImage.tagName === 'IMG') {
        // If carousel not yet active, wait for image to load before hiding overlay
        if (!this.carouselActive) {
          const preloadImg = new Image();
          preloadImg.onload = () => {
            mainImage.srcset = '';
            mainImage.src = image;
            this.activateCarousel();
            this.updateProgressBar();
          };
          preloadImg.src = image;
        } else {
          mainImage.srcset = '';
          mainImage.src = image;
          this.updateProgressBar();
        }
      }
    }
  }

  /**
   * Handle color swatch click
   */
  handleSwatchClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const swatch = e.currentTarget;
    const handle = swatch.dataset.productHandle;

    if (!this.swatchData.has(handle)) return;
    
    const data = this.swatchData.get(handle);

    // Update current product
    this.currentProductHandle = handle;
    this.currentVariantMapping = data.variants;
    this.currentImages = data.images;
    this.totalImages = data.images.length;
    this.currentImageIndex = 0;
    
    // Reset preload tracking for new product
    this.preloadedImages.clear();
    this.element.dataset.productUrl = data.url;
    // Update swatch styling
    this.swatches.forEach(s => {
      s.classList.remove('border-scheme-text');
      s.classList.add('border-scheme-text/20');
      s.removeAttribute('data-active');
    });
    swatch.classList.remove('border-scheme-text/20');
    swatch.classList.add('border-scheme-text');
    swatch.setAttribute('data-active', 'true');

    

    // Update ALL product links to point to new product
    const productLinks = this.element.querySelectorAll('a[href*="/products/"]');
    productLinks.forEach(link => {
      const currentHref = link.getAttribute('href');
      const newHref = currentHref.replace(/\/products\/[^?#/]+/, `/products/${handle}`);
      link.setAttribute('href', newHref);
    });

    // Update sizing panel with new product's variants
    this.updateSizingPanel(data);

    // Update badges
    this.updateBadges(data.tags);

    // Update price
    this.updatePrice(data);

    // Update images based on device
    if (this.isMobile && this.carouselTrack) {
      this.updateMobileCarousel();
    } else {
      this.updateAllImages();
    }

    // Keep sizing panel open on desktop
    if (!this.isMobile && this.sizingPanel) {
      this.showSizingPanel();
    }
  }

  /**
   * Mobile: Update carousel with new product images
   */
  updateMobileCarousel() {
    if (!this.carouselTrack) return;

    // Clear existing slides
    this.carouselTrack.innerHTML = '';

    // Create new slides
    this.currentImages.forEach((imageUrl, index) => {
      const slide = document.createElement('div');
      slide.className = 'collection-card-carousel-slide';
      slide.dataset.index = index;

      const img = document.createElement('img');
      img.src = imageUrl;
      img.className = 'collection-card-carousel-image';
      img.loading = index === 0 ? 'eager' : 'lazy';
      img.alt = '';

      slide.appendChild(img);
      this.carouselTrack.appendChild(slide);
    });

    // Reset scroll position
    this.carouselTrack.scrollLeft = 0;
    this.currentImageIndex = 0;
    
    // Update progress bar
    this.updateProgressBar();
    
    // Preload first few images
    this.preloadAdjacentImages(0);
  }

  /**
   * Update all images in the card (Desktop: main and hover)
   */
  updateAllImages() {
    if (this.totalImages === 0) return;

    const firstImage = this.currentImages[0];
    const secondImage = this.currentImages[1] || firstImage;

    if (this.mediaImages.length > 0) {
      const mainImage = this.mediaImages[0];
      if (mainImage && mainImage.tagName === 'IMG') {
        mainImage.srcset = '';
        mainImage.src = firstImage;
      }

      if (this.mediaImages.length > 1) {
        const hoverImage = this.mediaImages[1];
        if (hoverImage && hoverImage.tagName === 'IMG') {
          hoverImage.srcset = '';
          hoverImage.src = secondImage;
        }
      }
    }

    this.updateProgressBar();
  }

  /**
   * Update sizing panel buttons for current product
   */
  updateSizingPanel(data) {
    const sizesContainer = this.element.querySelector('.collection-card-sizes');
    if (!sizesContainer) return;

    const currentSizeButtons = this.element.querySelectorAll('.collection-card-size-btn');
    
    currentSizeButtons.forEach(btn => {
      const size = btn.dataset.size;
      const variantData = data.variants[size];
      
      if (variantData && variantData.id && variantData.available) {
        btn.dataset.variantId = variantData.id;
        btn.disabled = false;
        btn.classList.remove('is-unavailable', 'cursor-not-allowed');
        btn.classList.add('cursor-pointer');
      } else {
        btn.dataset.variantId = variantData ? variantData.id : '';
        btn.disabled = true;
        btn.classList.add('is-unavailable', 'cursor-not-allowed');
        btn.classList.remove('cursor-pointer');
      }
    });

    this.sizingButtons = this.element.querySelectorAll('.collection-card-size-btn');
  }

  /**
     * Update badge display based on product tags
     */
    updateBadges(productTags) {
    const badgeContainer = this.element.querySelector('.absolute.top-0.left-0');
    if (!badgeContainer) return;
    
    const tagBadges = this.element.dataset.tagBadges ? JSON.parse(this.element.dataset.tagBadges) : [];
    
    // Clear existing badges
    badgeContainer.innerHTML = '';
    
    let badgeFound = false;
    
    // Add badges for matching tags - only first match
    for (const tag of tagBadges) {
      const tagStripped = tag.trim();
      if (productTags.includes(tagStripped) && !badgeFound) {
        const badge = document.createElement('span');
        badge.className = 'rounded-button bg-scheme-accent text-scheme-accent-contrast';
        badge.setAttribute('style', 'padding: 4px 6px; font-size: 10px; font-weight: 300; letter-spacing: 0.5px; display: inline-flex; align-items: center; line-height: 1;');
        badge.textContent = tag;
        badgeContainer.appendChild(badge);
        badgeFound = true;
        break;
      }
    }
  }

  /**
   * Handle size button click - quick add to cart
   */
  async handleSizeClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const btn = e.currentTarget;
    if (btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        })
      });

      if (response.ok) {
        document.body.dispatchEvent(new CustomEvent('switch:cart:afteradditem'));

        if (this.isMobile) {
          this.closeSizingPanel();
        }
      }
    } catch (error) {
      // Error adding to cart
    }
  }

  /**
   * Show sizing panel
   */
  showSizingPanel() {
    if (!this.sizingPanel) return;
    this.sizingPanel.classList.remove('hidden', 'translate-y-full');
    this.sizingPanelOpen = true;
  }

  /**
   * Hide sizing panel
   */
  hideSizingPanel() {
    if (!this.sizingPanel) return;
    if (!this.isMobile) {
      this.sizingPanel.classList.add('hidden', 'translate-y-full');
      this.sizingPanelOpen = false;
    }
  }

  /**
   * Close sizing panel (for mobile)
   */
  closeSizingPanel(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!this.sizingPanel) return;
    this.sizingPanel.classList.add('translate-y-full');
    this.sizingPanelOpen = false;
  }

  /**
   * Open sizing panel (for mobile)
   */
  openSizingPanel(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.showSizingPanel();
  }

  /**
   * Update displayed image (Desktop)
   */
  updateImageDisplay() {
    if (this.totalImages === 0 || this.currentImages.length === 0) return;

    const image = this.currentImages[this.currentImageIndex];
    if (!image) return;

    if (this.mediaImages.length > 0) {
      const mainImage = this.mediaImages[0];
      if (mainImage && mainImage.tagName === 'IMG') {
        mainImage.srcset = '';
        mainImage.src = image;
      }
    }

    this.updateProgressBar();
  }

  /**
   * Update progress bar
   */
  updateProgressBar() {
    if (this.progressBar && this.totalImages > 0) {
      const progress = ((this.currentImageIndex + 1) / this.totalImages) * 100;
      this.progressBar.style.width = `${progress}%`;
    }
  }

      /**
   * Update price display when swatch changes
   */
  updatePrice(data) {
      const priceDiv = this.element.querySelector('.tile-content-wrapper .collection-card-price');
      if (!priceDiv) return;
      
      const price = parseInt(data.price) || 0;
      const compareAtPrice = parseInt(data.compareAtPrice) || 0;
      const symbol = data.currencySymbol || '$';
      const code = data.currencyCode || 'AUD';
      
      let priceHTML = symbol + (price / 100).toFixed(2) + ' ' + code;
      
      if (compareAtPrice > price) {
        priceHTML += ' <span style="text-decoration: line-through; color: rgba(0,0,0,0.5); margin-left: 0.5rem;">' + symbol + (compareAtPrice / 100).toFixed(2) + ' ' + code + '</span>';
      }
      
      priceDiv.innerHTML = priceHTML;
  }

    /**
  * Grey out compare-at prices on initial load and after updates
  */
  greyOutCompareAtPrice() {
    const priceContainer = this.element.querySelector('.tile-content-wrapper');
    if (!priceContainer) return;
    
    // Find all elements with strikethrough (inline style OR computed style)
    const allSpans = priceContainer.querySelectorAll('span, del, s');
    allSpans.forEach(el => {
      const computedStyle = window.getComputedStyle(el);
      const hasStrikethrough = 
        el.style.textDecoration?.includes('line-through') || 
        computedStyle.textDecoration?.includes('line-through');
      
      if (hasStrikethrough) {
        el.style.color = 'rgba(0,0,0,0.5)';
      }
    });
  }
}

/**
 * Initialize all collection cards on page
 */
export function initAllCollectionCards() {
  const cards = document.querySelectorAll('[data-collection-card="true"]');
  console.log('Found cards:', cards.length);
  cards.forEach((card, index) => {
    console.log('Initializing card', index, card);
    initCollectionCard(card);
  });
}

/**
 * Re-initialize on AJAX/Shopify section update
 */
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    initAllCollectionCards();
  });
}

/**
 * Initialize on page load
 */
console.log('About to initialize, readyState:', document.readyState);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllCollectionCards);
} else {
  initAllCollectionCards();
}

// Watch for dynamically added cards (e.g., "Complete the Look" sections)
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) { // Element node
        // Check if the added node is a card
        if (node.matches && node.matches('[data-collection-card="true"]')) {
          initCollectionCard(node);
        }
        // Check if the added node contains cards
        const nestedCards = node.querySelectorAll && node.querySelectorAll('[data-collection-card="true"]');
        if (nestedCards && nestedCards.length) {
          nestedCards.forEach(card => initCollectionCard(card));
        }
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});